import {
  filter,
  groupBy,
  includes,
  intersection,
  isEmpty,
  map,
  some,
  sortBy,
} from "lodash/fp";
import { DateTime } from "luxon";

import type {
  ReportSource,
  ReportView,
} from "@app/analytics/MeetingReports/types";
import type { Topic } from "@app/analytics/types";
import type { Tag, UserMeeting } from "@app/common/types";
import * as SessionUtils from "@app/common/utils/sessionUtils";

import type { Report } from "./types";

export const isTopicReport = (report: Report): report is Topic =>
  report && "lastMention" in report;

export const getReportSourceFilterValue = (
  source: ReportSource
): ReportSource => {
  if (source === "microsoft_graph_teams_chat_ingest") return "teams";
  return source;
};

const baseFilterFn = (meeting: UserMeeting) =>
  !(SessionUtils.isReadLite(meeting) && !meeting.ownerIsCurrentUser); // filter to only meetings the user can see

const meetingTagFilterFn = (tags: Tag[]) => (meeting: UserMeeting) =>
  baseFilterFn(meeting) &&
  some((tag) => some({ id: tag.id }, meeting.tags), tags);

const meetingSourceFilterFn =
  (sources: ReportSource[]) => (meeting: UserMeeting) =>
    baseFilterFn(meeting) &&
    ((SessionUtils.isUploadedSession(meeting) && includes("upload", sources)) ||
      includes(getReportSourceFilterValue(meeting.meetingPlatform), sources));

const topicTagFilterFn = (tags: Tag[]) => (topic: Topic) => {
  const tagIds = map("id", tags);
  return !isEmpty(intersection(topic.channelProviders, tagIds));
};

const topicSourceFilterFn = (sources: ReportSource[]) => (topic: Topic) => {
  return includes(getReportSourceFilterValue(topic.provider), sources);
};

const filterTopics = ({
  topics,
  sources,
  tags,
}: {
  sources: ReportSource[];
  topics: Topic[];
  tags: Tag[];
}) => {
  if (isEmpty(sources)) {
    return isEmpty(tags) ? topics : filter(topicTagFilterFn(tags), topics);
  }

  if (isEmpty(tags)) {
    return filter(topicSourceFilterFn(sources), topics);
  }

  return filter(
    (t) => topicTagFilterFn(tags)(t) && topicSourceFilterFn(sources)(t),
    topics
  );
};

export const filterMeetings = ({
  meetings,
  sources,
  tags = [],
}: {
  meetings: UserMeeting[];
  sources: ReportSource[];
  tags?: Tag[];
}) => {
  if (isEmpty(sources)) {
    return isEmpty(tags)
      ? meetings
      : filter(meetingTagFilterFn(tags), meetings);
  }

  if (isEmpty(tags)) {
    return filter(meetingSourceFilterFn(sources), meetings);
  }

  return filter(
    (m) => meetingTagFilterFn(tags)(m) && meetingSourceFilterFn(sources)(m),
    meetings
  );
};

export const filterReports = ({
  meetings,
  topics,
  reportViews,
  sources,
  tags = [],
}: {
  meetings: UserMeeting[];
  topics: Topic[];
  reportViews: ReportView[];
  sources: ReportSource[];
  tags?: Tag[];
}): Report[] => {
  const filteredMeetings = reportViews.includes("session")
    ? filterMeetings({ meetings, sources, tags })
    : [];

  const filteredTopics = reportViews.includes("topic")
    ? filterTopics({ topics, sources, tags })
    : [];

  return [...filteredMeetings, ...filteredTopics];
};

export const getReportDate = (report: Report) =>
  isTopicReport(report) ? report.lastMention : report.startTime;

export const isUnseen = (
  report: Report,
  storedLastSeenValue?: string | null
) => {
  if (!storedLastSeenValue) return false;
  const storedValue = DateTime.fromISO(storedLastSeenValue);
  return getReportDate(report) > storedValue;
};

export const groupRecurringMeetings = (
  allMeetings: UserMeeting[],
  recurringMeetings: Partial<UserMeeting>[]
) => {
  const meetingsBySeriesId = groupBy("seriesId", allMeetings);

  const seriesBySeriesId = recurringMeetings.reduce(
    (byIdMap, series) => {
      if (series.seriesId) {
        byIdMap[series.seriesId] = series;
      }
      return byIdMap;
    },
    {} as { [seriesId: string]: Partial<UserMeeting> }
  );

  const groupedMeetings: UserMeeting[] = [];
  const includedSeries: string[] = [];

  allMeetings.forEach((meeting) => {
    if (meeting.seriesId) {
      if (includedSeries.includes(meeting.seriesId)) return;

      const sortedMeetingInstances = sortBy(
        (meeting) => -meeting.startTime.toMillis(),
        meetingsBySeriesId[meeting.seriesId]
      );

      // combine the recurring meeting data with the data from a single
      // meeting instance to make a fully populated UserMeeting; use the
      // most recent meeting as the default for the series
      const recurringMeetingRollup = {
        ...sortedMeetingInstances[0],
        ...seriesBySeriesId[meeting.seriesId],
        id: meeting.seriesId,
        instances: sortedMeetingInstances,
      };

      includedSeries.push(meeting.seriesId);
      groupedMeetings.push(recurringMeetingRollup);
    } else {
      groupedMeetings.push(meeting);
    }
  });

  return groupedMeetings;
};
